
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import AdminRegistration from './community/AdminRegistration';

const Footer = () => {
  const { user, isAdmin, signOut } = useAuth();
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: adminEmail,
        password: adminPassword,
      });
      
      if (error) throw error;
      
      setShowAdminLogin(false);
      setAdminEmail('');
      setAdminPassword('');
      toast.success('Signed in successfully');
    } catch (error: any) {
      console.error('Admin login error:', error);
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  return (
    <footer className="bg-ypl-black text-white">
      <div className="container-custom py-12 bg-gray-950">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <p className="text-gray-300 mt-2">
              Awakening & Rebuilding A Nation
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="hover:text-ypl-green transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-ypl-green transition-colors">About Omo Oduduwa</Link></li>
              <li><Link to="/mission" className="hover:text-ypl-green transition-colors">Our Mission</Link></li>
              <li><Link to="/get-involved" className="hover:text-ypl-green transition-colors">Get Involved</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li><Link to="/news" className="hover:text-ypl-green transition-colors">News & Updates</Link></li>
              <li><Link to="/library" className="hover:text-ypl-green transition-colors">Library & Resources</Link></li>
              <li><a href="/heritage/" className="hover:text-ypl-green transition-colors">Cultural Heritage Archive</a></li>
              <li><Link to="/contact" className="hover:text-ypl-green transition-colors">Contact Us</Link></li>
              <li><a href="/ife/download.html" className="hover:text-ypl-green transition-colors">Join on the Ifẹ̀ app</a></li>
              {isAdmin && (
                <li><Link to="/admin" className="hover:text-ypl-green transition-colors">Admin Dashboard</Link></li>
              )}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <ul className="space-y-2">
              <li className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                <a href="mailto:support@oduaverse.com" className="hover:text-ypl-green transition-colors">support@oduaverse.com</a>
              </li>
              <li className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                <a href="tel:09134648310" className="hover:text-ypl-green transition-colors">09134648310</a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Omo Oduduwa. All rights reserved.
          </p>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <a href="https://omooduduwa.org" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-ypl-green transition-colors">
              🌍 Join
            </a>
            <a href="https://x.com/i/communities/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-ypl-green transition-colors">
              X Community
            </a>
            <a href="https://tiktok.com/@omooduduwa.or" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-ypl-green transition-colors">
              TikTok
            </a>
            
            {/* Admin section */}
            {isAdmin ? (
              <Link to="/admin" className="text-gray-400 hover:text-ypl-green transition-colors">
                Admin Dashboard
              </Link>
            ) : (
              <button 
                onClick={() => setShowAdminLogin(!showAdminLogin)} 
                className="text-gray-400 hover:text-ypl-green transition-colors cursor-pointer"
              >
                Admin
              </button>
            )}
          </div>
        </div>
        
        {/* Admin login modal */}
        {showAdminLogin && (
          <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Admin Login</h3>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAdminLogin(false)}
                    disabled={isLoggingIn}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="default"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? 'Logging in...' : 'Login'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
